import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Paper, Box, Grid, CircularProgress, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchFormConfig, submitFormData } from '../api'; // Importing the API methods

function DynamicFormPage() {
    const [formConfig, setFormConfig] = useState(null);  // Holds the configuration from API
    const [formData, setFormData] = useState({
        CSID: 'FrishCartRAJ',
        doubleWrapperAttribute1: '',
        val1: '',
        val2: '',
        itemList: [{ val3: '', val4: '' }] // Default single row for itemListConfigData
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Fetch form configuration from the API
    useEffect(() => {
        const getConfig = async () => {
            try {
                setLoading(true); // Start loading while fetching config
                const response = await fetchFormConfig(); // Fetch config
                setFormConfig(response.data);  // Save the configuration from API
                setLoading(false); // End loading after fetching
            } catch (err) {
                setError('Failed to fetch form configuration');
                setLoading(false); // End loading if an error occurs
            }
        };

        getConfig();
    }, []);

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle dynamic table changes (for each row in the itemListConfigData)
    const handleTableChange = (index, e) => {
        const { name, value } = e.target;
        const newList = [...formData.itemList];
        newList[index][name] = value;
        setFormData({ ...formData, itemList: newList });
    };

    // Add new row to the dynamic table
    const addRow = () => {
        setFormData(prevData => ({
            ...prevData,
            itemList: [...prevData.itemList, { val3: '', val4: '' }] // Default empty values
        }));
    };

    // Delete a row from the dynamic table
    const deleteRow = (index) => {
        const newList = formData.itemList.filter((_, i) => i !== index);
        setFormData({ ...formData, itemList: newList });
    };

    // Submit the form data
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {

            const payload = {
                data: {
                    CSID: 'FrishCartRAJ', // Hardcoded ID
                    doubleWrapperAttribute1: parseFloat(formData.doubleWrapperAttribute1), // User's input for 'new' value (double)

                    // Dynamically adding keys for tableData1 from formData
                    ...formConfig?.tableData1.reduce((acc, field) => {
                        if (field.correspondingTableColumnName && formData[field.correspondingTableColumnName] !== undefined) {
                            acc[field.correspondingTableColumnName] = formData[field.correspondingTableColumnName]; // Add dynamic key from tableData1
                        }
                        return acc;
                    }, {}),

                    // Dynamically building the 'list' for tableData2
                    itemList: formData.itemList.map(row => {
                        return formConfig?.itemListConfigData.reduce((acc, field) => {
                            if (field.correspondingTableColumnName && row[field.correspondingTableColumnName] !== undefined) {
                                acc[field.correspondingTableColumnName] = row[field.correspondingTableColumnName]; // Add dynamic key from tableData2
                            }
                            return acc;
                        }, {});
                    })
                }
            };


            // Post form data via an API call
            const response = await submitFormData(payload); // POST API call
            setLoading(false);
            navigate('/success'); // Redirect on success (modify as needed)
        } catch (err) {
            setError('Failed to submit form');
            setLoading(false);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Grid container justifyContent="center" sx={{ mt: 4, width: '100%' }}>
            <Grid item xs={12} sm={10} md={8} lg={6}> {/* Increased container width */}
                <Paper elevation={6} sx={{ p: 3 }}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Dynamic Form
                    </Typography>
                    {error && <Typography color="error" align="center" sx={{ mb: 2 }}>{error}</Typography>}
                    <form onSubmit={handleSubmit}>


                        {/* Render fields for tableData1 */}
                        {formConfig?.tableData1.map((field, index) => (
                            <Box key={index} mb={2}>
                                {field.dataType !== 'MultipartFile' ? (
                                    <TextField
                                        fullWidth
                                        label={field.placeHolderText}
                                        variant="outlined"
                                        name={field.correspondingTableColumnName}
                                        value={formData[field.correspondingTableColumnName] || ''}
                                        onChange={handleChange}
                                        disabled={loading}
                                        type={field.dataType === 'Long' ? 'number' : 'text'}
                                    />) : ''}
                            </Box>
                        ))}

                        {/* Dynamic Table for itemListConfigData */}
                        <Box mb={2}>
                            <Typography variant="h6">Dynamic Table</Typography>
                            {/* Plus icon to add rows */}
                            <IconButton onClick={addRow} sx={{ mb: 2 }}>
                                <AddIcon />
                            </IconButton>

                            {/* Render rows for itemListConfigData */}
                            {formData.itemList.map((row, rowIndex) => (
                                <Grid container spacing={2} key={rowIndex} alignItems="center">
                                    {formConfig?.itemListConfigData.map((field, fieldIndex) => (
                                        <Grid item xs={3} key={fieldIndex}> {/* Adjusted to 3 columns */}
                                            <TextField
                                                fullWidth
                                                label={field.placeHolderText}
                                                variant="outlined"
                                                name={field.correspondingTableColumnName}
                                                value={row[field.correspondingTableColumnName] || ''}
                                                onChange={(e) => handleTableChange(rowIndex, e)}
                                                required
                                                disabled={loading}
                                                type={field.dataType === 'Long' ? 'number' : 'text'}
                                            />
                                        </Grid>
                                    ))}
                                    {/* Delete icon to remove the row */}
                                    <Grid item xs={1}>
                                        <IconButton onClick={() => deleteRow(rowIndex)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>

                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Receipt Final Total"
                                variant="outlined"
                                type="number"
                                name="doubleWrapperAttribute1"
                                value={formData.doubleWrapperAttribute1}
                                onChange={handleChange}
                                required
                                disabled={loading}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </form>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default DynamicFormPage;
